/**
 * @file PendingBubble.tsx
 * @description Моё сообщение, которое ещё не дошло до сервера
 * (chat-window-design.md §5): «Отправляется…» со значком часов или
 * «Не отправлено. Текст сохранён» с красной рамкой и [Повторить] [Удалить].
 */

import { Clock, RotateCcw, Trash2 } from "lucide-react";

import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils";
import { CHAT_TEXT } from "./chat-texts";
import type { PendingMessage } from "./useMessageSender";

interface Props {
  pending: PendingMessage;
  wide: boolean;
  onRetry: (tempId: string) => void;
  onRemove: (tempId: string) => void;
}

export function PendingBubble({ pending, wide, onRetry, onRemove }: Props) {
  const failed = pending.status === "failed";
  return (
    <div className="flex flex-col items-end gap-1">
      <div
        className={cn(
          "min-w-0 rounded-xl rounded-br-[4px] px-3 py-2 text-sm",
          wide ? "max-w-[min(75%,560px)]" : "max-w-[75%]",
          failed ? "bg-surface-lowest text-on-surface ring-2 ring-destructive" : "bg-primary/70 text-on-primary",
        )}
      >
        {pending.isMarketing ? (
          <Badge variant="warning" className="mb-1">
            {CHAT_TEXT.advertising}
          </Badge>
        ) : null}
        <span className="whitespace-pre-wrap break-words">{pending.body}</span>
      </div>
      {failed ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span role="alert" className="text-xs text-destructive">
            {CHAT_TEXT.failed}
          </span>
          <Button variant="outline" size="sm" className="min-h-12 lg:min-h-8" onClick={() => onRetry(pending.tempId)}>
            <RotateCcw className="size-4" aria-hidden />
            {CHAT_TEXT.retry}
          </Button>
          <Button variant="ghost" size="sm" className="min-h-12 lg:min-h-8" onClick={() => onRemove(pending.tempId)}>
            <Trash2 className="size-4" aria-hidden />
            {CHAT_TEXT.delete}
          </Button>
        </div>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs text-on-surface-variant">
          <Clock className="size-3.5" aria-hidden />
          {CHAT_TEXT.sending}
        </span>
      )}
    </div>
  );
}
